import React, { useEffect, useState } from 'react';
import { Box, Stack, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserType } from '../../../interfaces/userType.enum';
import { DashboardService } from '../../../services/dashboard.service';
import { PeopleSummary, HeadcountPoint } from '../../../interfaces/dashboard.interface';
import { WidgetDef, WidgetProps } from './types';
import { useSummaryFetch, WidgetLoadError } from './useSummaryFetch';
import HBars from '../charts/HBars';
import Columns from '../charts/Columns';
import StatTile from '../charts/StatTile';
import useKpiTargets from '../useKpiTargets';

const PEOPLE_ROLES = [UserType.InstitutionAdmin, UserType.InstitutionManager, UserType.Accountant];

const PeopleWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { t } = useTranslation('dashboard');
    const [d, setD] = useState<PeopleSummary | null>(null);
    const targets = useKpiTargets(institutionId);
    useEffect(() => {
        let live = true;
        DashboardService.getPeopleSummary(institutionId).then(x => { if (live) setD(x); }).catch(() => {});
        return () => { live = false; };
    }, [institutionId]);
    if (!d) return <CircularProgress size={20} />;
    const headcountTarget = targets['people.headcount'];
    const headcountMeter = headcountTarget != null && headcountTarget > 0
        ? Math.max(0, Math.min(100, (d.active / headcountTarget) * 100))
        : undefined;
    return (
        <Box>
            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                <StatTile label={t('widgets.people.active')} value={d.active} sub={t('widgets.ofTotal', { total: d.total })} tone="good" target={headcountTarget} meter={headcountMeter} />
                <StatTile label={t('widgets.people.inactive')} value={d.inactive} />
                <StatTile label={t('widgets.people.noDepartment')} value={d.noDepartmentCount} tone={d.noDepartmentCount > 0 ? 'warning' : 'default'} />
            </Stack>
            <HBars data={d.byDepartment.map(b => ({ label: b.label, value: b.count }))} colorIndex={2} />
        </Box>
    );
};

const loadPeopleGrowth = (institutionId: string): Promise<HeadcountPoint[]> =>
    DashboardService.getPeopleGrowth(institutionId);

const PeopleGrowthWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { t } = useTranslation('dashboard');
    const { data: d, failed } = useSummaryFetch(loadPeopleGrowth, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    // The series is cumulative and month-zero-filled server-side; an empty array only happens
    // when the institution has never had a member (entity-absence, decision 14).
    if (d.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                {t('widgets.people.noMembers')}
            </Typography>
        );
    }
    return (
        <Box>
            <Columns data={d.map(p => ({ label: p.month, value: p.headcount }))} colorIndex={2} />
            <Typography variant="caption" color="text.secondary">{t('widgets.people.growthCaption')}</Typography>
        </Box>
    );
};

export const peopleWidgets: WidgetDef[] = [
    { id: 'people.summary', module: 'people', titleKey: 'widgets.titles.peopleSummary', subtitleKey: 'widgets.subtitles.peopleSummary', roles: PEOPLE_ROLES, size: 'md', component: PeopleWidget },
    { id: 'people.growth', module: 'people', titleKey: 'widgets.titles.peopleGrowth', subtitleKey: 'widgets.subtitles.peopleGrowth', roles: PEOPLE_ROLES, size: 'lg', component: PeopleGrowthWidget },
];
